import React, { useState, useEffect } from 'react';
import { Route, Switch, Redirect, useHistory, useLocation } from 'react-router-dom';

import './App.css';
import axios from './axios';
import config from './config';

// Components
import LoginForm from './components/LoginForm';
import LoginOverlay from './components/LoginOverlay';
import RegisterPage from './components/RegisterPage';
import HomePage from './components/HomePage/HomePage';
import ObjectDashboard from './components/Objects/ObjectDashboard';
import BrokerDashboard from './components/Broker/BrokerDashboard';
import SellerFormWrapper from './components/Objectinfo/SellerFormWrapper';
import ObjectForm from './components/Objectinfo/ObjectForm';
import SellerDataDetail from './components/Seller/SellerDataDetail';
import NewConversation from './components/Conversation/NewConversation';
import Inbox from './components/Conversation/Inbox';
import ConversationForm from './components/Conversation/ConversationForm';
import FAQ from './components/FAQ/FAQ';
import Terms from './components/Terms/Terms';
import Privacy from './components/Privacy/Privacy';
import BrokerStart from './components/BrokerStart/BrokerStart';
import SellerLandingPage from './components/SellerLandingPage/SellerLandingPage';
import SellerDashboard from './components/Seller/SellerDashboard';
import Header from './components/Header/Header';
import BrokerProfile from './components/Broker/BrokerProfile';
import BrokerSearch from './components/BrokerSearch/BrokerSearch';
import CookieConsent from './components/CookieConsent/CookieConsent';
import ValuationPage from './components/Valuation/ValuationPage';
import ValuationResult from './components/Valuation/ValuationResult';
import BodexPlusPage from './components/BodexPlus/BodexPlus';

// Analytics
import { trackPageView, isBodexEsHost } from './utils/analytics';
import { storeTrackingParams } from './utils/tracking';
import { isBodexDkHostname, isBodexNoHostname } from './utils/spanishMarket';

// Cookie service
import {
  hasUserConsent,
  acceptAllCookies,
  rejectNonEssentialCookies,
  saveUserConsent,
  setCookies,
  applyStoredCookiePreferences
} from './services/cookieService';

// Blog Components
import BlogList from './components/Blog/BlogList';
import BlogPost from './components/Blog/BlogPost';
import BlogAdminLogin from './components/Blog/BlogAdminLogin';
import BlogAdmin from './components/Blog/BlogAdmin';
import BlogPostForm from './components/Blog/BlogPostForm';
import AccountVerification from './components/AccountVerification/AccountVerification';
import PrintLead from './components/PrintLead/PrintLead';
import VerifyLead from './components/VerifyLead/VerifyLead';
import GradeLead from './components/GradeLead/GradeLead';

function App() {
  const [loggedIn, setLoggedIn] = useState(false);
  const [userType, setUserType] = useState('');
  const [showLoginOverlay, setShowLoginOverlay] = useState(false);
  const [refreshUnreadCount, setRefreshUnreadCount] = useState(0);
  const [showCookieConsent, setShowCookieConsent] = useState(false);
  const [pendingRedirectUrl, setPendingRedirectUrl] = useState(null);
  const backend_url = config.backend_url;
  const history = useHistory();
  const location = useLocation();
  
  // Background form state
  const [backgroundObject, setBackgroundObject] = useState('');
  const [backgroundImages, setBackgroundImages] = useState([]);
  
  // Dummy handle submit function for the background form
  const dummyHandleSubmit = (e) => {
    if (e) e.preventDefault();
    return false;
  };

  useEffect(() => {
    const storedLoggedIn = localStorage.getItem('loggedIn');
    const storedUserType = localStorage.getItem('userType');
    const authToken = localStorage.getItem('authToken');
    
    if (storedLoggedIn) {
      setLoggedIn(JSON.parse(storedLoggedIn));
      setUserType(storedUserType);
    }
    
    // Set Authorization header if token exists in localStorage
    if (authToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
    }
    
    // Check if user has already provided cookie consent
    const userHasConsent = hasUserConsent();
    if (!userHasConsent) {
      setShowCookieConsent(true);
    } else {
      // Apply stored preferences
      applyStoredCookiePreferences();
    }

    // Add event listener for show-cookie-consent event from FooterWrapper
    const handleShowCookieConsent = () => {
      setShowCookieConsent(true);
      // Remove the localStorage flag after handling it
      localStorage.removeItem('show_cookie_consent');
    };

    // Add event listener for login state changes from SellerFormWrapper
    const handleLoginStateChange = () => {
      const newLoggedIn = localStorage.getItem('loggedIn');
      const newUserType = localStorage.getItem('userType');
      if (newLoggedIn === 'true') {
        setLoggedIn(true);
        setUserType(newUserType);
      }
    };

    window.addEventListener('show-cookie-consent', handleShowCookieConsent);
    window.addEventListener('login-state-changed', handleLoginStateChange);

    // Check if the show_cookie_consent flag is set in localStorage
    if (localStorage.getItem('show_cookie_consent')) {
      handleShowCookieConsent();
    }

    return () => {
      window.removeEventListener('show-cookie-consent', handleShowCookieConsent);
      window.removeEventListener('login-state-changed', handleLoginStateChange);
    };
  }, []);

  // Initialize analytics only in production
  useEffect(() => {
    if (process.env.NODE_ENV === 'production' && typeof window.gtag === 'function') {
      // Configure Google Ads
      window.gtag('config', 'AW-17166076911');
      
      // Configure Google Analytics
      window.gtag('config', 'G-YW1FXHGCHH');
      
      console.log('Production environment detected. Google Analytics and Ads configured.');
    }
  }, []);

  // Add effect for Google Analytics page view tracking
  useEffect(() => {
    // Track the initial page view on component mount
    trackPageView(location.pathname, document.title);
  }, [location]);

  // Persist Google Ads / UTM tracking parameters whenever they appear
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const search = location.search || '';
    if (!search) return;
    const params = new URLSearchParams(search);
    storeTrackingParams(params);
  }, [location.search]);

  // Add effect to trigger refresh of unread count when navigating away from conversation
  useEffect(() => {
    // Only refresh when logged in and navigated away from a conversation
    // Use a small delay to prevent excessive API calls
    let timeoutId;
    if (loggedIn && !location.pathname.includes('/conversation/')) {
      timeoutId = setTimeout(() => {
        setRefreshUnreadCount(prev => prev + 1);
      }, 500); // Add a debounce delay
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [location.pathname, loggedIn]);

  // Check if user is trying to access a conversation URL while not logged in
  useEffect(() => {
    const pathMatch = location.pathname.match(/^\/conversation\/(\d+)$/);
    
    if (pathMatch && !loggedIn) {
      // User is trying to access a conversation URL while not logged in
      // Store the intended destination and show login overlay
      setPendingRedirectUrl(location.pathname);
      setShowLoginOverlay(true);
    }
  }, [location.pathname, loggedIn]);

  // Function to forcefully cancel any active BankID sessions
  const cancelBankIdSessions = async () => {
    try {
      
      
      // Get the current orderRef if it exists
      const orderRef = localStorage.getItem('bankid_orderRef');
      
      if (orderRef) {
        // Make a direct API call to cancel this specific BankID session
        // This is similar to what the "Starta om processen" button does
        try {
          // Using a fake/invalid collect call with the orderRef might trigger session cleanup on server
          await axios.post(
            `${backend_url}/bankid/collect`,
            { order_ref: orderRef, cancel: true },
            { withCredentials: true }
          );
        } catch (err) {
          // The call might fail, but that's expected
          
        }
      }
      
      // Cancel any active polling and timers
      const pollIntervals = window.setInterval(() => {}, 100000);
      for (let i = 0; i < pollIntervals; i++) {
        window.clearInterval(i);
      }
      
      // Clear all BankID related localStorage data immediately
      localStorage.removeItem('bankid_orderRef');
      localStorage.removeItem('bankid_timestamp');
      localStorage.removeItem('bankid_overlay');
      localStorage.removeItem('bankid_launch_time');
      localStorage.removeItem('bankid_url');
      localStorage.removeItem('bankid_last_focus');
      
      
    } catch (error) {
      console.error("Error during BankID session cancellation:", error);
    }
  };

  const handleLogout = async () => {
    try {
      // Set flag to prevent automatic BankID start on next login
      localStorage.setItem('skip_bankid_autostart', 'true');
      
      // First, forcefully cancel any outstanding BankID sessions
      await cancelBankIdSessions();
      
      // Try to call server-side logout
      const response = await axios.post(
        `${backend_url}/api/logout`,
        {},
        { withCredentials: true }
      );
      
      if (response.status === 200 && response.data.message === 'Logged out successfully') {
        
      } else {
        console.error('Unexpected response during logout:', response.data);
      }
    } catch (error) {
      console.error('There was an error logging out:', error);
    } finally {
      // Always perform these cleanup steps regardless of server response
      
      // Update app state
      setLoggedIn(false);
      setUserType('');
      
      // Clear all authentication data
      localStorage.removeItem('loggedIn');
      localStorage.removeItem('userType');
      localStorage.removeItem('userId');
      localStorage.removeItem('authToken');
      
      // Clear user data
      localStorage.removeItem('firstname');
      localStorage.removeItem('lastname');
      localStorage.removeItem('personnummer');
      
      // One final check for any BankID related data
      localStorage.removeItem('bankid_orderRef');
      localStorage.removeItem('bankid_timestamp');
      localStorage.removeItem('bankid_overlay');
      localStorage.removeItem('bankid_launch_time');
      localStorage.removeItem('bankid_url');
      localStorage.removeItem('bankid_last_focus');
      
      // Note: Do NOT remove the skip_bankid_autostart flag we set at the beginning
      
      // Remove Authorization header
      delete axios.defaults.headers.common['Authorization'];
      
      // Add a slight delay before redirecting to ensure cleanup completes
      setTimeout(() => {
        // Redirect to home page
        history.push('/');
        
        // Skip the reload to preserve our autostart flag - React navigation is enough
      }, 100);
    }
  };

  // Cookie consent handlers
  const handleAcceptAllCookies = () => {
    acceptAllCookies();
    setCookies({
      essential: true,
      functional: true,
      analytics: true,
      marketing: true
    });
    setShowCookieConsent(false);
  };

  const handleRejectNonEssential = () => {
    rejectNonEssentialCookies();
    setCookies({
      essential: true,
      functional: false,
      analytics: false,
      marketing: false
    });
    setShowCookieConsent(false);
  };

  const handleSavePreferences = (preferences) => {
    saveUserConsent(preferences);
    setCookies(preferences);
    setShowCookieConsent(false);
  };

  return (
    <div className="app-container">
      <Header
        handleLogout={handleLogout}
        loggedIn={loggedIn}
        userType={userType}
        setShowLoginOverlay={setShowLoginOverlay}
        refreshTrigger={refreshUnreadCount}
      />

      {/* When overlay is shown, display ObjectForm in the background with a blur filter */}
      {showLoginOverlay && (
        <div className="background-form-container">
          <ObjectForm
            handleSubmit={dummyHandleSubmit}
            object={backgroundObject}
            setObject={setBackgroundObject}
            images={backgroundImages}
            setImages={setBackgroundImages}
          />
        </div>
      )}

      {showLoginOverlay && (
        <LoginOverlay
          onLogin={(type, id, firstname, lastname) => {
            setLoggedIn(true);
            setUserType(type);
            localStorage.setItem('loggedIn', true);
            localStorage.setItem('userType', type);
            localStorage.setItem('userId', id);
            
            // Store name information if provided
            if (firstname) localStorage.setItem('firstname', firstname);
            if (lastname) localStorage.setItem('lastname', lastname);
            
            setShowLoginOverlay(false);
            
            // If there's a pending redirect URL (like a conversation), navigate to it
            if (pendingRedirectUrl) {
              history.push(pendingRedirectUrl);
              setPendingRedirectUrl(null);
            }
          }}
          onClose={() => {
            setShowLoginOverlay(false);
            // If user closes overlay without logging in and there's a pending redirect, 
            // redirect them to home instead
            if (pendingRedirectUrl) {
              history.push('/');
              setPendingRedirectUrl(null);
            }
          }}
        />
      )}

      <div className={`content-wrapper ${showLoginOverlay ? 'hidden' : ''} ${isBodexEsHost() ? 'bodex-es' : ''} ${(typeof window !== 'undefined' && isBodexNoHostname(window.location.hostname) && !isBodexEsHost()) ? 'bodex-no' : ''} ${(typeof window !== 'undefined' && isBodexDkHostname(window.location.hostname) && !isBodexEsHost()) ? 'bodex-dk' : ''}`}>
        <Switch>
          <Route exact path="/">
            {!loggedIn && (
                <HomePage />
            )}
            {loggedIn && userType === 'seller' && <SellerLandingPage />}
            {loggedIn && userType === 'broker' && <BrokerDashboard />}
          </Route>

          <Route path="/seller-latest" component={SellerLandingPage} />

          <Route path="/login">
            <LoginForm
              onLogin={(type, id, firstname, lastname) => {
                setLoggedIn(true);
                setUserType(type);
                localStorage.setItem('loggedIn', true);
                localStorage.setItem('userType', type);
                localStorage.setItem('userId', id);

                // Store name information if provided
                if (firstname) localStorage.setItem('firstname', firstname);
                if (lastname) localStorage.setItem('lastname', lastname);
              }}
            />
          </Route>

          <Route path="/register">
            <RegisterPage
              onLogin={(type, id, firstname, lastname) => {
                setLoggedIn(true);
                setUserType(type);
                localStorage.setItem('loggedIn', true);
                localStorage.setItem('userType', type);
                localStorage.setItem('userId', id);
                
                // Store name information if provided
                if (firstname) localStorage.setItem('firstname', firstname);
                if (lastname) localStorage.setItem('lastname', lastname);
              }}
            />
          </Route>

          <Route path="/verify-account">
            <AccountVerification
              onLogin={(type, id, firstname, lastname) => {
                setLoggedIn(true);
                setUserType(type);
                localStorage.setItem('loggedIn', true);
                localStorage.setItem('userType', type);
                localStorage.setItem('userId', id);

                if (firstname) localStorage.setItem('firstname', firstname);
                if (lastname) localStorage.setItem('lastname', lastname);
              }}
            />
          </Route>

          <Route path="/brokerstart" component={BrokerStart} />

          <Route path="/seller-data/:id" component={SellerDataDetail} />
          
          {/* Allow object-form access without login - login will be handled within the form submission flow */}
          <Route path="/object-form" component={SellerFormWrapper} />
          
          {/* Valuation feature - accessible to all users */}
          <Route path="/valuation" component={ValuationPage} />
          <Route path="/valuation-result" component={ValuationResult} />
          <Route path="/bodex-plus" component={BodexPlusPage} />
          
          {/* Print lead - accessible to all users */}
          <Route path="/print-lead" component={PrintLead} />
          
          {/* Verify lead - accessible to all users */}
          <Route path="/verify-lead" component={VerifyLead} />
          
          {/* Grade lead - accessible to all users */}
          <Route path="/grade-lead" component={GradeLead} />
          
          {/* Blog routes - accessible to all users whether logged in or not */}
          <Route path="/blog" exact component={BlogList} />
          <Route path="/blog/:postId" component={BlogPost} />
          <Route path="/faq" component={FAQ} />
          <Route path="/terms" component={Terms} />
          <Route path="/privacy" component={Privacy} />

          {loggedIn && (
            <>
              <Route path="/new-conversation" component={NewConversation} />
              <Route path="/inbox">
                <Inbox onMessageRead={() => setRefreshUnreadCount(prev => prev + 1)} />
              </Route>
              <Route path="/conversation/:conversationId" 
                render={(props) => (
                  <ConversationForm 
                    conversationId={props.match.params.conversationId} 
                    onMessageSent={() => setRefreshUnreadCount(prev => prev + 1)}
                  />
                )} 
              />
              <Route path="/seller-dashboard" component={SellerDashboard} />
              <Route path="/object-dashboard" component={ObjectDashboard} />
              <Route path="/broker/:brokerId" component={BrokerProfile} />
              <Route path="/broker-search" component={BrokerSearch} />
              <Route path="/faq" component={FAQ} />
            </>
          )}

          {/* Admin blog routes */}
          <Route path="/adminblog" exact component={BlogAdminLogin} />
          <Route path="/adminblog/posts" component={BlogAdmin} />
          <Route path="/adminblog/post/new" component={BlogPostForm} />
          <Route path="/adminblog/post/edit/:postId" component={BlogPostForm} />
        </Switch>
      </div>

      {showCookieConsent && (
        <CookieConsent
          onAcceptAll={handleAcceptAllCookies}
          onRejectNonEssential={handleRejectNonEssential}
          onSavePreferences={handleSavePreferences}
          onClose={() => setShowCookieConsent(false)}
        />
      )}
    </div>
  );
}

export default App;
